/**
 * useVisualizerModeSwitch — Switches the visualizer display mode for a panel.
 *
 * Mode-switch model: switching a visualizer mode updates the UI store AND
 * creates/removes pipeline steps so that visualizer state survives
 * save/load/tab-switch.  "Raw" is the "no visualizer" mode and does NOT
 * create a pipeline step.
 *
 * Backward-compatible: `getToolIdForMode` and `AVAILABLE_VISUALIZER_MODES`
 * are still exported for code that needs to map modes ↔ tool IDs
 * (e.g. saved pipelines with visualizer pipeline steps).
 */

import { useCallback } from 'react';

import type { IPipelineStep } from '@/interfaces/IPipeline';
import {
  getToolIdForMode,
  TOOL_TO_MODE,
  VISUALIZER_MANIFEST,
} from '@/lib/visualizerManifest';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { type PanelRole,useVisualizerStore, type VisualizerMode } from '@/store/useVisualizerStore';

/** Infer panelRole for legacy steps without it — head viz = input, tail = output */
function inferPanelRole(step: IPipelineStep, pipeline: IPipelineStep[]): PanelRole {
  const firstRealIdx = pipeline.findIndex(s => !TOOL_TO_MODE[s.toolId]);
  const stepIdx = pipeline.indexOf(step);
  return (firstRealIdx === -1 || stepIdx < firstRealIdx) ? 'input' : 'output';
}

// ─── Reverse mapping: mode → tool ID (derived from manifest) ────────────────

export { getToolIdForMode };

// ─── Available modes for the dropdown (derived from manifest) ────────────────

export interface VisualizerModeOption {
  mode: VisualizerMode;
  label: string;
  icon: string;
  toolId: string;
}

/** Pre-built list of modes that have a corresponding tool — derived from manifest. */
export const AVAILABLE_VISUALIZER_MODES: VisualizerModeOption[] = VISUALIZER_MANIFEST.map(
  (entry) => ({
    mode: entry.mode,
    label: entry.label,
    icon: entry.icon,
    toolId: entry.toolId,
  }),
);

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseVisualizerModeSwitchReturn {
  /** Switch to the given visualizer mode (no-op if already active). */
  switchMode: (mode: VisualizerMode) => void;
  /** Remove a mode from the QuickSwitchBar AND its backing pipeline step. */
  removeMode: (mode: VisualizerMode) => void;
  /** Reset to auto-detected mode (pass autoMode), or 'raw' if no auto mode. */
  resetMode: (autoMode?: VisualizerMode) => void;
  /** Always true — mode switching is always available. */
  canAdd: boolean;
  /** True when the current panel mode is not 'raw'. */
  isExplicitActive: boolean;
}

/**
 * Hook to switch visualizer display mode for a panel.
 * Switching mode updates the visualizer store for immediate UI AND
 * creates a backing pipeline step so the visualizer survives save/load.
 *
 * @param panelRole - Which panel ('input' | 'output') this controls.
 */
export function useVisualizerModeSwitch(panelRole: PanelRole): UseVisualizerModeSwitchReturn {
  const currentMode = useVisualizerStore(
    (s) => s[panelRole === 'input' ? 'inputPanel' : 'outputPanel'].mode,
  );

  const switchMode = useCallback(
    (mode: VisualizerMode) => {
      // Update store for immediate UI
      useVisualizerStore.getState().setMode(panelRole, mode);

      // If mode is 'raw', no pipeline step needed
      if (mode === 'raw') return;

      // Mode → tool ID mapping
      const toolId = getToolIdForMode(mode);
      if (!toolId) return;

      // Check if a pipeline step for this mode+panel already exists — no-op if so
      const pipeline = useEditorStore.getState().pipeline;
      const alreadyExists = pipeline.some(
        (s) => s.toolId === toolId && (s.panelRole === panelRole || (!s.panelRole && inferPanelRole(s, pipeline) === panelRole)),
      );
      if (alreadyExists) return;

      // Snapshot output so pipeline mutation doesn't clear it
      const { output, outputType } = useEditorStore.getState();
      const { stepResults, errors } = useExecutionStore.getState();

      // Add the visualizer step at end of leading viz block (input) or at tail (output)
      if (panelRole === 'input') {
        const currentPipeline = useEditorStore.getState().pipeline;
        const firstRealIdx = currentPipeline.findIndex(s => !TOOL_TO_MODE[s.toolId]);
        const insertIdx = firstRealIdx === -1 ? currentPipeline.length : firstRealIdx;
        useEditorStore.getState().insertToolAt(toolId, insertIdx, 'input');
      } else {
        useEditorStore.getState().addTool(toolId, 'tail', 'output');
      }

      // Restore output — visualizer mode switch is display-only
      useEditorStore.setState({ output, outputType });
      useExecutionStore.setState({
        stepResults,
        errors,
        error: errors._pipeline ?? null,
      });
    },
    [panelRole],
  );

  const removeMode = useCallback(
    (mode: VisualizerMode) => {
      // Remove from QuickSwitchBar UI
      useVisualizerStore.getState().removeActiveMode(panelRole, mode);

      // If current mode was the removed one, fall back to 'raw'
      const vizState = useVisualizerStore.getState();
      const panelConfig = panelRole === 'input' ? vizState.inputPanel : vizState.outputPanel;
      if (panelConfig.mode === mode) {
        useVisualizerStore.getState().setMode(panelRole, 'raw');
      }

      // Remove the corresponding pipeline step (snapshot/restore output)
      const toolId = getToolIdForMode(mode);
      if (!toolId) return;
      const editorState = useEditorStore.getState();
      const { output, outputType } = editorState;
      const { stepResults, errors } = useExecutionStore.getState();
      const step = editorState.pipeline.find(
        (s) => s.toolId === toolId && (s.panelRole === panelRole || (!s.panelRole && inferPanelRole(s, editorState.pipeline) === panelRole)),
      );
      if (step) {
        useEditorStore.getState().removeTool(step.id);
        useEditorStore.setState({ output, outputType });
        useExecutionStore.setState({
          stepResults,
          errors,
          error: errors._pipeline ?? null,
        });
      }
    },
    [panelRole],
  );

  const resetMode = useCallback((autoMode?: VisualizerMode) => {
    useVisualizerStore.getState().setMode(panelRole, autoMode ?? 'raw');
  }, [panelRole]);

  return {
    switchMode,
    removeMode,
    resetMode,
    canAdd: true,
    isExplicitActive: currentMode !== 'raw',
  };
}
